/**
 * Workforce profiling: manual percentage overrides per employee per package per report month.
 * `value` null or missing row means "use baseline" (e.g. labour-hour derived); `0` is explicit.
 */
import { z } from "zod";
import { daysInMonth } from "../../utils/date";
/**
 * The divisor a project uses when it has not set one of its own
 * (`project.workforce_profile_full_time_monthly_hours`). Every conversion below
 * takes the project's value as an optional argument and falls back to this, so
 * a caller with no project in hand still gets the historical behaviour.
 */
export const WORKFORCE_PROFILE_FULL_TIME_MONTHLY_HOURS = 160;
/** A project's setting if it is a usable positive number, else the default. */
export const resolveWorkforceProfileFullTimeMonthlyHours = (fullTimeMonthlyHours) => typeof fullTimeMonthlyHours === "number" &&
    Number.isFinite(fullTimeMonthlyHours) &&
    fullTimeMonthlyHours > 0
    ? fullTimeMonthlyHours
    : WORKFORCE_PROFILE_FULL_TIME_MONTHLY_HOURS;
export const workforceProfilePercentToHours = (value, fullTimeMonthlyHours) => (value / 100) *
    resolveWorkforceProfileFullTimeMonthlyHours(fullTimeMonthlyHours);
/**
 * Hours -> the stored `value` unit, for seeding the profiling grid from actual
 * labour hours: a full-time month is `100`, not its hour count.
 *
 * The reverse direction of {@link workforceProfilePercentToHours}, and lossy —
 * a left inverse for whole percents only. Rounding is deliberate: the grid
 * renders and accepts whole percents, so an unrounded seed would differ from
 * the number on screen and be re-read as a manual override.
 *
 * Not capped at 100: a month of real overtime seeds above it.
 *
 * Both guards below are load-bearing, for different inputs. The floor catches a
 * negative SUM, which `hammertech_hours_modifier` makes reachable. The
 * `Number.isFinite` branch catches NaN, which `Math.max` would pass straight
 * through — and NaN is the worse case, because it compares unequal to itself
 * and would therefore be persisted as a spurious override.
 */
export const workforceProfileHoursToPercent = (hours, fullTimeMonthlyHours) => Number.isFinite(hours)
    ? Math.max(0, Math.round((hours /
        resolveWorkforceProfileFullTimeMonthlyHours(fullTimeMonthlyHours)) *
        100))
    : 0;
export const workforceProfileSchema = z.object({
    contractor_employee_id: z.string(),
    contractor_package_id: z.string(),
    created: z.string(),
    project_id: z.string(),
    report_id: z.string(),
    value: z.number().nullable(),
    workforce_profile_id: z.string()
});
/** Payload for upsert (server may assign workforce_profile_id on insert). */
export const workforceProfileUpsertSchema = z.object({
    contractor_employee_id: z.string(),
    contractor_package_id: z.string(),
    project_id: z.string(),
    report_id: z.string(),
    value: z.number().nullable(),
    workforce_profile_id: z.string().optional()
});
/**
 * Grid month key (`yyyy-MM`) → DB `report_id` (last day of month, `yyyy-MM-dd`),
 * consistent with labour hours and other report month ids.
 */
export const monthKeyToWorkforceProfileReportId = (monthKey) => {
    const trimmed = monthKey.trim();
    if (/^\d{4}-\d{2}$/.test(trimmed)) {
        const year = Number.parseInt(trimmed.slice(0, 4), 10);
        const month = Number.parseInt(trimmed.slice(5, 7), 10);
        const lastDay = daysInMonth(month, year);
        return `${trimmed}-${String(lastDay).padStart(2, "0")}`;
    }
    return trimmed;
};
/** DB `report_id` → grid month key `yyyy-MM`. */
export const workforceProfileReportIdToMonthKey = (reportId) => reportId.length >= 7 ? reportId.slice(0, 7) : reportId;
